import whenDomReady from 'when-dom-ready';

const HIDDEN_CLASS = 'ghwp-reveal';
const VISIBLE_CLASS = 'ghwp-reveal--visible';
const SELECTOR = '#content.entry-content > *, .ghwp-pre-footer';

void whenDomReady().then(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    // The legacy bundle may run in browsers without IntersectionObserver;
    // without it (or without JS at all) no element is ever hidden.
    if (!('IntersectionObserver' in window)) {
        return;
    }

    const candidates = Array.from(
        document.querySelectorAll<HTMLElement>(SELECTOR)
    );

    // Elements within or above the current viewport at init time stay visible,
    // so above-the-fold content never fades and anchor-link or scroll-restored
    // page loads are handled gracefully.
    const viewportHeight = window.innerHeight;
    const belowFold = candidates.filter(
        (el) => el.getBoundingClientRect().top >= viewportHeight
    );

    if (belowFold.length === 0) {
        return;
    }

    const pending = new Set<Element>(belowFold);

    const reveal = (el: Element): void => {
        el.classList.add(VISIBLE_CLASS);
        observer.unobserve(el);
        pending.delete(el);
    };

    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    reveal(entry.target);
                }
            }
        },
        // Trigger when an element's top edge crosses the line at 85% of the
        // viewport height — unlike an element-relative threshold, this also
        // works for sections taller than the viewport.
        { rootMargin: '0px 0px -15% 0px', threshold: 0 }
    );

    for (const el of belowFold) {
        el.classList.add(HIDDEN_CLASS);
        observer.observe(el);
    }

    // Elements whose top can never rise above that line (short last sections,
    // the pre-footer) would otherwise stay invisible forever: reveal whatever
    // is left once the viewport gets close to the document bottom.
    const onScroll = (): void => {
        if (pending.size === 0) {
            window.removeEventListener('scroll', onScroll);
            return;
        }

        const nearBottom =
            window.scrollY + window.innerHeight >=
            document.documentElement.scrollHeight - window.innerHeight * 0.15;

        if (nearBottom) {
            pending.forEach(reveal);
            window.removeEventListener('scroll', onScroll);
        }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
});
